import AmountDatesFormStep from "@/components/dashboard/edit/AmountsDatesFormStep";
import ExteriorFormStep from "@/components/dashboard/edit/ExteriorFormStep";
import ImageUploadFormStep from "@/components/dashboard/edit/ImageUploadFormStep";
import InteriorFormStep from "@/components/dashboard/edit/InteriorFormStep";
import LocationFormStep from "@/components/dashboard/edit/LocationFormStep";
import PropertyDescriptionsFormStep from "@/components/dashboard/edit/PropertyDescriptionsFormStep";
import RemotePropertyFormStep from "@/components/dashboard/edit/RemotePropertyFormStep";
import RoomsFormStep from "@/components/dashboard/edit/RoomsFormStep";
import MultiStepForm from "@/components/shared/form/MultiStepForm";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDashboardProperty } from "@/hooks/useDashboardProperty";
import { condoPropertyFormSchema, PropertyForm, propertyFormSchema, PropertyUpdate } from "@/types/property";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
export const Route = createFileRoute('/_protected/dashboard/edit')({
  component: PropertyEditFormPage,
})

function PropertyEditFormPage() {
  useEffect(() => {
    document.title = 'Edit Listing | Swim Homes';
  }, []);

  const navigate = useNavigate();
  const { property, updateProperty } = useDashboardProperty();
  const [schema, setSchema] = useState(() => property.is_freehold ? propertyFormSchema : condoPropertyFormSchema);
  const propertyIdRef = useRef<string>(property.property_id);

  const form = useForm<PropertyForm>({
    defaultValues: property,
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });

  useEffect(() => {
    if (property.property_id !== propertyIdRef.current) {
      propertyIdRef.current = property.property_id;
      form.reset(property);
      console.log(property);
      setSchema(property.is_freehold ? propertyFormSchema : condoPropertyFormSchema);
    }
  }, [property, form]);

  const isFreehold = form.watch("is_freehold");

  useEffect(() => {
    const newSchema = isFreehold ? propertyFormSchema : condoPropertyFormSchema;
    setSchema(newSchema);
  }, [isFreehold]);

  const propertyType = isFreehold ? form.watch("freehold_property_type") : form.watch("condo_property_type");
  const isWaterfront = form.watch("is_waterfront");
  const isRemote = isWaterfront || ['Farm', 'Vacant Land', 'Rural Residential'].includes(propertyType ?? '');

  const getSteps = useCallback(() => {
    return [
      LocationFormStep,
      AmountDatesFormStep,
      ExteriorFormStep,
      ...(isRemote ? [RemotePropertyFormStep] : []),
      InteriorFormStep,
      RoomsFormStep,
      PropertyDescriptionsFormStep,
      ImageUploadFormStep,
    ];
  }, [isRemote]);

  const lastSavedAt = useRef<Date | null>(null);

  const saveForm = useCallback(async (data: PropertyUpdate) => {
    await updateProperty(data);
    lastSavedAt.current = new Date();
  }, [updateProperty]);

  const onSubmit = useCallback(async (data: PropertyUpdate) => {
    await saveForm({ ...data, status: 'w4_payment' });
    navigate({ to: '/dashboard' });
  }, [saveForm, navigate]);

  // Simple autosave every second
  const [isSaving, setIsSaving] = useState(false);
  const lastSavedState = useRef(JSON.stringify(form.getValues()));

  useEffect(() => {
    const timer = setInterval(async () => {
      const formData = form.getValues();
      const formDataString = JSON.stringify(formData);

      if (lastSavedState.current === formDataString) return;

      lastSavedState.current = formDataString;
      setIsSaving(true);
      await saveForm(formData);
      setIsSaving(false);
    }, 1000);

    return () => clearInterval(timer);
  }, [form, saveForm]);

  return (
    <ScrollArea className="w-full">
      <MultiStepForm
        form={form}
        schema={schema}
        steps={getSteps()}
        onSubmit={onSubmit}
        isSaving={isSaving}
        lastSaved={lastSavedAt.current}
      />
    </ScrollArea>
  );
}
